import { BannerStack } from '@hub/app/banners';
import { Footer } from '@hub/app/shell/Footer';
import { Header } from '@hub/app/shell/Header';
import { SkipToMainLink } from '@hub/app/shell/SkipToMainLink';
import { useMatches } from '@hub/router';
import { cn } from '@platform/ui';

interface AppShellProps {
  children: React.ReactNode;
  hideBanners?: boolean;
}

export function AppShell({ children, hideBanners }: AppShellProps) {
  // Read here, like Header and Footer read their own, so each layout does not have to re-thread them.
  const theme = useMatches({ select: (matches) => matches.at(-1)?.staticData?.theme });
  const shellWidth = useMatches({ select: (matches) => matches.at(-1)?.staticData?.shellWidth });
  // Per-slot instead of cascading, so content keeps the light tokens (white cards on the dark page).
  const chromeScope = theme === 'dark-canvas' ? 'surface-dark' : undefined;

  return (
    <div
      className={cn(
        'flex min-h-screen flex-col bg-canvas',
        theme === 'dark' && 'surface-dark',
        theme === 'dark-canvas' && 'bg-canvas-dark',
        shellWidth === 'wide' && 'shell-wide',
      )}
    >
      <SkipToMainLink targetId="hub-main" />

      <Header className={chromeScope} />

      {/* overflow-x-clip lives here, below the sticky header, so it can't become the header's
          sticky-positioning containing block (an ancestor with a non-visible overflow axis
          forces the other axis to `auto`, which turns it into that containing block). */}
      <div className="flex min-h-0 flex-1 flex-col overflow-x-clip">
        {!hideBanners && (
          <BannerStack scope="global" className={cn('mx-auto w-full px-3 pt-4 md:max-w-(--shell-content-max) md:px-0', chromeScope)} />
        )}

        <div className="flex min-h-0 w-full flex-1 flex-col sm:flex-row">
          <main
            id="hub-main"
            tabIndex={-1}
            className="mx-auto flex w-full min-w-0 flex-1 flex-col px-3 pb-8 md:max-w-(--shell-content-max) md:px-0"
          >
            {children}
          </main>
        </div>

        <Footer className={chromeScope} />
      </div>
    </div>
  );
}
